const LEGEND_CLASSES = 4
const JENKS_MAX_SAMPLE = 2000

function positiveValues(values) {
  return values
    .map(Number)
    .filter((value) => Number.isFinite(value) && value > 0)
}

function sortedPositiveValues(values) {
  return positiveValues(values).sort((a, b) => a - b)
}

// Matches ApexMaps quantileBreaks (equal-count class boundaries).
function quantileBreaks(sorted, classes) {
  const breaks = []

  if (!sorted.length || classes < 2) return breaks

  for (let index = 1; index < classes; index++) {
    const position = (sorted.length - 1) * (index / classes)
    const lowerIndex = Math.floor(position)
    const upperIndex = Math.min(sorted.length - 1, lowerIndex + 1)
    breaks.push(sorted[lowerIndex] + (sorted[upperIndex] - sorted[lowerIndex]) * (position - lowerIndex))
  }

  return breaks
}

// Matches ApexMaps jenksBreaks (sampled when the dataset is large).
function jenksBreaks(sorted, classes, maxSample = JENKS_MAX_SAMPLE) {
  if (sorted.length <= classes || classes < 2) {
    return { breaks: quantileBreaks(sorted, classes), sampled: false }
  }

  let values = sorted
  let sampled = false

  if (sorted.length > maxSample) {
    values = new Array(maxSample)
    const step = (sorted.length - 1) / (maxSample - 1)

    for (let index = 0; index < maxSample; index++) {
      values[index] = sorted[Math.round(index * step)]
    }

    sampled = true
  }

  const valueCount = values.length
  const matrix = Array.from({ length: valueCount + 1 }, () => new Array(classes + 1).fill(0))
  const varianceSums = Array.from({ length: valueCount + 1 }, () => new Array(classes + 1).fill(Infinity))

  for (let classIndex = 1; classIndex <= classes; classIndex++) {
    matrix[1][classIndex] = 1
    varianceSums[1][classIndex] = 0
  }

  for (let upper = 2; upper <= valueCount; upper++) {
    let sum = 0
    let sumSquares = 0
    let count = 0

    for (let lower = 1; lower <= upper; lower++) {
      const valueIndex = upper - lower + 1
      const value = values[valueIndex - 1]
      count += 1
      sum += value
      sumSquares += value * value
      const variance = sumSquares - (sum * sum) / count

      if (valueIndex > 1) {
        for (let classIndex = 2; classIndex <= classes; classIndex++) {
          if (varianceSums[upper][classIndex] >= variance + varianceSums[valueIndex - 1][classIndex - 1]) {
            matrix[upper][classIndex] = valueIndex
            varianceSums[upper][classIndex] = variance + varianceSums[valueIndex - 1][classIndex - 1]
          }
        }
      }
    }

    matrix[upper][1] = 1
    varianceSums[upper][1] = sumSquares - (sum * sum) / count
  }

  const breaks = []
  let upperBound = valueCount

  for (let classIndex = classes; classIndex >= 2; classIndex--) {
    const breakIndex = matrix[upperBound][classIndex] - 1
    breaks.unshift(values[breakIndex])
    upperBound = breakIndex
  }

  return { breaks, sampled }
}

function classBoundsForScale(sorted, scaleConfig) {
  if (sorted.length === 0) {
    return [{ from: 0, to: 1 }, { from: 1, to: 1 }]
  }

  const min = sorted[0]
  const max = sorted[sorted.length - 1]
  const uniqueValues = [...new Set(sorted)]

  if (scaleConfig.type === "ordinal") {
    return uniqueValues.map((value) => ({ from: value, to: value }))
  }

  let breaks = []

  if (scaleConfig.type === "threshold") {
    breaks = [...scaleConfig.breaks].sort((left, right) => left - right)
  } else if (scaleConfig.type === "jenks") {
    breaks = jenksBreaks(sorted, scaleConfig.classes).breaks
  } else {
    breaks = quantileBreaks(sorted, scaleConfig.classes || 2)
  }

  const bounds = [min, ...breaks, max]

  return bounds.slice(0, -1).map((from, index) => ({
    from,
    to: bounds[index + 1]
  }))
}

// ApexMaps classifies with `value >= break`, so adjacent legend tiers share a
// boundary in `from`/`to` even though only one class owns that value. Display
// exclusive upper bounds so labels like "1 – 39" / "39 – 300" do not overlap.
export function formatNonOverlappingClassLabel(from, to, nextFrom, formatCount) {
  const fromInt = Math.round(from)
  const toInt = Math.round(to)

  if (!Number.isFinite(fromInt) || !Number.isFinite(toInt)) {
    return formatCount(to ?? from)
  }

  if (fromInt === toInt) {
    return formatCount(fromInt)
  }

  const nextFromInt = nextFrom == null ? null : Math.round(nextFrom)
  const displayTo = nextFromInt === toInt ? toInt - 1 : toInt

  if (fromInt >= displayTo) {
    return formatCount(fromInt)
  }

  return `${formatCount(fromInt)} – ${formatCount(displayTo)}`
}

export function legendLabelsForScaleConfig(values, scaleConfig, formatCount) {
  const bounds = classBoundsForScale(sortedPositiveValues(values), scaleConfig)

  return bounds.map((bound, index) =>
    formatNonOverlappingClassLabel(
      bound.from,
      bound.to,
      bounds[index + 1]?.from,
      formatCount
    )
  )
}

// Quantile splits by feature count, not value range. On skewed contract counts
// (many departments at 1, a few higher) that produces duplicate legend tiers
// such as "1 – 1" repeated across classes.
export function scaleConfigForValues(values, { maxClasses = LEGEND_CLASSES } = {}) {
  const positive = positiveValues(values)
  const uniqueValues = [...new Set(positive)].sort((a, b) => a - b)

  if (uniqueValues.length === 0) {
    return { type: "quantile", classes: 2 }
  }

  if (uniqueValues.length === 1) {
    return { type: "ordinal" }
  }

  if (uniqueValues.length <= maxClasses) {
    return {
      type: "threshold",
      breaks: uniqueValues.slice(1)
    }
  }

  return {
    type: "jenks",
    classes: maxClasses
  }
}

export { LEGEND_CLASSES }
